<template>
    <div class="commission-detail-wrapper">
        <div class="detail-container" v-if="commission">
            <!-- 상단 헤더 (PC/모바일 공통) -->
            <div class="detail-header">
                <button class="back-arrow-btn" type="button" @click="$router.push('/commission')" title="뒤로가기">
                    ←
                </button>
                <button type="button" class="share-btn" title="공유하기" @click="showShareModal = true">
                    <img src="@/assets/images/ios_share.png" alt="공유" class="share-icon" />
                </button>
            </div>

            <div class="detail-content">
                <div>
                    <div class="detail-poster-container">
                        <div class="poster-blur-background" :style="posterBackgroundStyle"></div>
                        <div class="poster-wrapper">
                            <div v-if="!posterLoaded" class="poster-skeleton">
                                <div class="skeleton-shimmer"></div>
                            </div>
                            <img v-if="commission.thumbnailUrl" :src="commission.thumbnailUrl" :alt="commission.name"
                                class="detail-poster" :class="{ 'poster-loaded': posterLoaded }" @load="onPosterLoad" />
                            <div v-else class="no-image-placeholder">
                                <span>썸네일 없음</span>
                            </div>
                        </div>
                    </div>
                    <!-- 링꾹 구매: 구매하기 → /commission-purchase / 외부 링크: 구매하기 → 외부 이동 / 그 외: 문의하기 (하단 고정) -->
                    <div class="reserve-btn mobile-reserve-btn">
                        <template v-if="visitorCanPurchase">
                            <router-link v-if="commission.purchaseType === 'linkook'" :to="{
                                name: 'CommissionPurchaseForm',
                                params: { id: commission.id },
                            }" class="reserve-btn-inner">
                                구매하기
                            </router-link>
                            <a v-else-if="commission.purchaseType === 'link' && commission.purchaseLink"
                                :href="commission.purchaseLink" target="_blank" rel="noopener noreferrer"
                                class="reserve-btn-inner">
                                구매하기
                            </a>
                            <button v-else type="button" class="reserve-btn-inner linkook-reserve"
                                @click="toggleCommentSection">
                                문의하기
                            </button>
                        </template>
                        <div v-else class="reserve-btn-inner reserve-btn-inner--disabled" aria-disabled="true">
                            승인 후 이용 가능
                        </div>
                    </div>
                </div>

                <div class="detail-info">
                    <div v-if="commission.status === 'draft' && canEdit" class="draft-notice-commission">
                        <div class="draft-notice-icon">📝</div>
                        <div class="draft-notice-text">
                            <span class="draft-notice-label">임시저장 상태입니다</span>
                            <p class="draft-notice-desc">등록을 완료하면 관리자 승인 후 공개됩니다.</p>
                        </div>
                    </div>
                    <div v-else-if="commission.status === 'published' && commission.approved === false"
                        class="approval-pending-notice-commission">
                        <div class="approval-pending-icon-commission">⏳</div>
                        <div class="approval-pending-text-commission">
                            <span class="approval-pending-label-commission">승인 대기중입니다</span>
                            <p class="approval-pending-desc-commission">관리자 승인 후 목록에 노출됩니다.</p>
                        </div>
                    </div>
                    <div class="detail-title">
                        <div class="title-left">
                            <h1>{{ commission.name }}</h1>
                            <a v-if="commission.link" class="detail-more-link" @click="openExternal">자세히보기</a>
                        </div>
                        <div class="title-actions">
                            <div v-if="canEdit" class="manage-btn-wrapper" @focusout="onManageWrapperFocusout">
                                <button type="button" class="manage-settings-btn"
                                    @click="showManageDropdown = !showManageDropdown" title="관리 메뉴">
                                    <img src="@/assets/images/settings.png" alt="관리" class="settings-icon" />
                                </button>
                                <div v-if="showManageDropdown" class="manage-dropdown" @mousedown.prevent>
                                    <button type="button" class="manage-dropdown-item"
                                        @click="openEditForm(); showManageDropdown = false">
                                        수정하기
                                    </button>
                                    <button v-if="commission.purchaseType === 'linkook'" type="button"
                                        class="manage-dropdown-item" @click="goToCommissionFormAdmin">
                                        구매폼 관리
                                    </button>
                                    <button v-if="commission.purchaseType === 'linkook'" type="button"
                                        class="manage-dropdown-item" @click="goToCommissionPurchases">
                                        구매 관리
                                    </button>
                                    <button type="button" class="manage-dropdown-item manage-dropdown-item--danger"
                                        @click="confirmDeleteCommission">
                                        삭제
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="info-table">
                        <div class="info-row" v-if="commission.category">
                            <div class="info-label">카테고리</div>
                            <div class="info-value">
                                {{ categoryLabel(commission.category) }}
                            </div>
                        </div>
                        <!-- 관리자용 작성자 정보 -->
                        <div class="info-row"
                            v-if="userStore.auth === 'admin' && (commission.createdBy || commission.createdByNickname)">
                            <div class="info-label">작성자</div>
                            <div class="info-value">
                                <span>{{ commission.createdByNickname || "알 수 없음" }}</span>
                                <span class="admin-sub-text">({{ commission.createdBy }})</span>
                            </div>
                        </div>
                        <div class="info-row" v-if="commission.createdAt">
                            <div class="info-label">등록일</div>
                            <div class="info-value">
                                {{ formatDate(commission.createdAt) }}
                            </div>
                        </div>
                    </div>

                    <div v-if="userStore.auth === 'admin'" class="admin-section-commission">
                        <div class="admin-section-header-commission" @click="adminSectionOpen = !adminSectionOpen">
                            <span class="admin-section-title-commission">🔧 관리자 메뉴</span>
                            <span class="admin-section-toggle-commission">{{ adminSectionOpen ? "▲" : "▼" }}</span>
                        </div>
                        <div v-show="adminSectionOpen" class="admin-section-content-commission">
                            <div class="admin-info-table-commission">
                                <div class="admin-info-row-commission">
                                    <div class="admin-info-label-commission">승인 상태</div>
                                    <div class="admin-info-value-commission">
                                        <span class="approval-status-commission" :class="{
                                            'approval-status-commission--ok': commission.approved,
                                            'approval-status-commission--wait': !commission.approved,
                                        }">
                                            {{ commission.approved ? "승인됨" : "승인 대기" }}
                                        </span>
                                        <button type="button" class="approval-toggle-btn-commission"
                                            @click="toggleApproval" :disabled="approvalLoading">
                                            {{ approvalLoading ? "처리중..." : (commission.approved ? "승인 취소" : "승인") }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 탭 섹션 -->
            <div v-if="hasTabsSection" class="tabs-section">
                <div class="tab-navigation">
                    <button v-if="hasCommissionDetailTab" class="tab-button" :class="{ active: activeTab === 'images' }"
                        @click="activeTab = 'images'">
                        커미션 상세
                    </button>
                    <button v-if="hasSalesInfoTab" class="tab-button" :class="{ active: activeTab === 'sales' }"
                        @click="activeTab = 'sales'">
                        판매 정보
                    </button>
                </div>
                <div class="tab-content">
                    <!-- 커미션 상세: 설명 + 세부 이미지 + 하단 영상 -->
                    <div v-if="activeTab === 'images'" class="tab-panel">
                        <!-- Toast UI Viewer — 보관용. TinyMCE를 메인으로 사용 중이라 숨김 -->
                        <div v-if="false && commission.description" class="commission-description-block">
                            <div ref="descriptionViewerMount" class="description-viewer-host"></div>
                        </div>

                        <!-- 커미션 설명 (TinyMCE · HTML) -->
                        <div v-if="commission.descriptionHtml"
                            class="commission-description-block commission-description-html">
                            <div class="description-html-host" v-html="commission.descriptionHtml"></div>
                        </div>
                        <div v-if="hasDetailImages" class="detail-image-section">
                            <div v-for="(url, idx) in detailImageUrls" :key="idx" class="detail-image-container">
                                <img :src="url" :alt="'커미션 상세 ' + (idx + 1)" class="detail-image" />
                            </div>
                        </div>
                        <div v-if="hasYoutubeVideos" class="commission-youtube-block">
                            <h2 class="artist-section-title">영상</h2>
                            <div class="youtube-videos-grid">
                                <div v-for="(video, idx) in commission.youtubeVideos" :key="video.vid || idx"
                                    class="youtube-video-item">
                                    <div class="youtube-embed-wrapper">
                                        <iframe :src="`https://www.youtube.com/embed/${video.vid}`" frameborder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowfullscreen loading="lazy" class="youtube-embed"></iframe>
                                    </div>
                                    <div class="youtube-video-meta">
                                        <p class="youtube-video-title">{{ video.title }}</p>
                                        <p class="youtube-video-channel">{{ video.channel }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- 판매 정보 탭 (LiveDetail 판매 정보 탭과 동일 양식) -->
                    <div v-if="activeTab === 'sales'" class="tab-panel">
                        <div class="booking-content">
                            <div class="booking-item">
                                <h3 class="booking-label">구매 방식</h3>
                                <p class="booking-value">유형 | {{ purchaseTypeLabel }}</p>
                            </div>
                            <template v-if="commission.purchaseType === 'linkook'">
                                <div class="booking-item">
                                    <h3 class="booking-label">정산 정보</h3>
                                    <p class="booking-value">정산 은행 | {{ commission.settlementBank || "—" }}</p>
                                    <p class="booking-value">정산 계좌 | {{ commission.settlementAccountNumber || "—" }}</p>
                                    <p class="booking-value">예금주 | {{ commission.settlementAccountHolder || "—" }}</p>
                                </div>
                                <div v-if="commission.sellerName" class="booking-item">
                                    <h3 class="booking-label">판매자 정보</h3>
                                    <p class="booking-value">판매자명 | {{ commission.sellerName }}</p>
                                </div>
                            </template>
                            <div v-else-if="commission.purchaseType === 'dm'" class="booking-item">
                                <h3 class="booking-label">문의 안내</h3>
                                <p class="booking-value">하단 문의하기를 통해 작가에게 연락해 주세요.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- 커미션 수정 모달 -->
        <CommissionForm :is-visible="showEditForm" mode="update" :commission-data="commissionEditData"
            @close-popup="closeEditForm" />

        <!-- 댓글 섹션 오버레이 -->
        <div v-if="showCommentSection" class="comment-overlay" @click="toggleCommentSection"></div>

        <!-- 댓글 섹션 (하단에서 올라오는 패널) -->
        <div :class="['comment-panel', { 'comment-panel-open': showCommentSection }]">
            <div class="comment-panel-header">
                <h3 class="comment-panel-title">문의하기</h3>
                <button class="comment-panel-close" @click="toggleCommentSection">×</button>
            </div>
            <div class="comment-panel-content">
                <CommentSection v-if="commission?.id" :board-id="commission.id" :collection-type="'commissions'"
                    :post-writer-email="commission?.createdBy || ''" />
            </div>
        </div>

        <ShareOptionsModal :visible="showShareModal" @close="showShareModal = false" @select="handleShareMethod" />
    </div>
</template>

<script>
import { doc, getDoc } from "firebase/firestore";
import { ref } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { firestore, storage } from "/firebaseConfig";
import { useUserStore } from "@/store/user";
import CommissionForm from "@/components/modal/form/CommissionForm.vue";
import CommentSection from "@/components/CommentSection.vue";
import { setPageMeta, shareViaKakao, shareViaTwitter } from "@/utils/pageMeta";
import { deleteCommission, toggleCommissionApproval } from "@/utils/write/commission";
import { createNotification } from "@/utils/write/alarm";
import { trackShare } from "@/utils/activityTracker";
import ShareOptionsModal from "@/components/modal/ShareOptionsModal.vue";
import ToastuiViewer from "@toast-ui/editor/dist/toastui-editor-viewer";
import "@toast-ui/editor/dist/toastui-editor-viewer.css";

export default {
    name: "CommissionDetail",
    components: {
        CommissionForm,
        CommentSection,
        ShareOptionsModal,
    },
    data() {
        return {
            loading: true,
            commission: null,
            showEditForm: false,
            showCommentSection: false,
            showShareModal: false,
            posterLoaded: false,
            showManageDropdown: false,
            activeTab: "",
            detailImageUrls: [],
            adminSectionOpen: false,
            approvalLoading: false,
        };
    },
    computed: {
        userStore() {
            return useUserStore();
        },
        canEdit() {
            if (!this.commission) return false;
            return this.userStore.auth === "admin" ||
                (this.userStore.isLogin && this.commission.createdBy === this.userStore.email);
        },
        visitorCanPurchase() {
            if (!this.commission) return false;
            if (this.userStore.auth === "admin") return true;
            if (this.canEdit) return true;
            return this.commission.approved !== false;
        },
        commissionEditData() {
            if (!this.commission) return null;
            return {
                id: this.commission.id,
                name: this.commission.name,
                category: this.commission.category,
                description: this.commission.description,
                link: this.commission.link,
                thumbnail: this.commission.thumbnail,
                thumbnailUrl: this.commission.thumbnailUrl,
                detailImages: this.commission.detailImages || [],
                youtubeVideos: this.commission.youtubeVideos || [],
                purchaseType: this.commission.purchaseType || "",
                purchaseLink: this.commission.purchaseLink || "",
                settlementBank: this.commission.settlementBank || "",
                settlementAccountNumber: this.commission.settlementAccountNumber || "",
                settlementAccountHolder: this.commission.settlementAccountHolder || "",
                sellerName: this.commission.sellerName || "",
                status: this.commission.status || "published",
            };
        },
        purchaseTypeLabel() {
            const map = {
                link: "외부 링크",
                linkook: "링꾹 구매",
                dm: "DM 문의",
                form: "신청폼",
            };
            return map[this.commission?.purchaseType] || "—";
        },
        hasSalesInfoTab() {
            return !!this.commission?.purchaseType;
        },
        /** 설명/세부 이미지/영상 중 하나라도 있을 때 '커미션 상세' 탭 표시 */
        hasCommissionDetailTab() {
            return !!this.commission?.description || this.hasDetailImages || this.hasYoutubeVideos;
        },
        hasTabsSection() {
            return this.hasCommissionDetailTab || this.hasSalesInfoTab;
        },
        hasDetailImages() {
            return this.detailImageUrls.length > 0;
        },
        hasYoutubeVideos() {
            return this.commission?.youtubeVideos?.length > 0;
        },
        posterBackgroundStyle() {
            if (this.commission?.thumbnailUrl) {
                return {
                    backgroundImage: `url(${this.commission.thumbnailUrl})`,
                };
            }
            return { background: "#1a1a2e" };
        },
    },
    async created() {
        await this.fetchCommission();
    },
    beforeUnmount() {
        this.destroyDescriptionViewer();
    },
    watch: {
        activeTab(newTab) {
            if (newTab === "images") {
                this.$nextTick(() => this.mountDescriptionViewer());
            } else {
                this.destroyDescriptionViewer();
            }
        },
    },
    methods: {
        async fetchCommission() {
            try {
                this.loading = true;
                const id = this.$route.params.id;
                if (!id) return;

                const snap = await getDoc(doc(firestore, "commissions", id));
                if (!snap.exists()) {
                    this.commission = null;
                    return;
                }
                const data = snap.data();
                let thumbnailUrl = data.thumbnail || "";
                const originalThumbnail = data.thumbnail || "";

                if (thumbnailUrl && !thumbnailUrl.startsWith("http")) {
                    try {
                        const imageRef = ref(storage, `commission/${thumbnailUrl}`);
                        thumbnailUrl = await getDownloadURL(imageRef);
                    } catch {
                        thumbnailUrl = "";
                    }
                }

                this.commission = {
                    id,
                    category: data.category || "",
                    name: data.name || "",
                    link: data.link || "",
                    description: data.description || "",
                    thumbnail: originalThumbnail,
                    thumbnailUrl,
                    detailImages: data.detailImages || [],
                    youtubeVideos: Array.isArray(data.youtubeVideos) ? data.youtubeVideos : [],
                    purchaseType: data.purchaseType || "",
                    purchaseLink: data.purchaseLink || "",
                    settlementBank: data.settlementBank || "",
                    settlementAccountNumber: data.settlementAccountNumber || "",
                    settlementAccountHolder: data.settlementAccountHolder || "",
                    sellerName: data.sellerName || "",
                    createdBy: data.createdBy || "",
                    createdByNickname: data.createdByNickname || "",
                    createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
                    status: data.status || "published",
                    approved: data.approved,
                };

                // 세부 이미지 URL 로드
                await this.loadDetailImageUrls();

                // 기본 탭 설정 (watch.activeTab에서 Viewer 마운트)
                if (this.hasCommissionDetailTab) this.activeTab = "images";
                else if (this.hasSalesInfoTab) this.activeTab = "sales";

                setPageMeta({
                    title: this.commission.name,
                    description: this.commission.description || undefined,
                    image: thumbnailUrl || undefined,
                    url: typeof window !== "undefined" ? window.location.href : undefined,
                });
            } catch (e) {
                this.commission = null;
            } finally {
                this.loading = false;
            }
        },
        onPosterLoad() {
            this.posterLoaded = true;
        },
        mountDescriptionViewer() {
            const el = this.$refs.descriptionViewerMount;
            const markdown = this.commission?.description || "";
            if (!el) return;
            this.destroyDescriptionViewer();
            if (!markdown.trim()) return;
            try {
                this._descriptionViewer = new ToastuiViewer({
                    el,
                    initialValue: markdown,
                });
            } catch (e) {
                console.error("ToastUI Viewer mount 실패:", e);
            }
        },
        destroyDescriptionViewer() {
            if (!this._descriptionViewer) return;
            try {
                this._descriptionViewer.destroy();
            } catch (_) {
                /* noop */
            }
            this._descriptionViewer = null;
        },
        openEditForm() {
            this.showEditForm = true;
        },
        goToCommissionFormAdmin() {
            if (!this.commission?.id) return;
            this.showManageDropdown = false;
            this.$router.push(`/commission-form-admin/${this.commission.id}`);
        },
        goToCommissionPurchases() {
            if (!this.commission?.id) return;
            this.showManageDropdown = false;
            this.$router.push(`/commission-purchases/${this.commission.id}`);
        },
        closeEditForm() {
            this.showEditForm = false;
            this.fetchCommission();
        },
        async confirmDeleteCommission() {
            this.showManageDropdown = false;
            if (!this.canEdit || !this.commission) return;
            const confirmed = await this.$confirm(
                "정말로 이 커미션을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다."
            );
            if (!confirmed) return;
            try {
                console.log("[CommissionDelete] request", {
                    id: this.commission.id,
                    createdBy: this.commission.createdBy,
                    currentEmail: this.userStore.email,
                    auth: this.userStore.auth,
                });
                await deleteCommission(this.commission.id, this.commission);
                this.$alert("커미션이 삭제되었습니다.");
                this.$router.push("/commission");
            } catch (error) {
                console.error("커미션 삭제 오류:", error, {
                    id: this.commission?.id,
                    createdBy: this.commission?.createdBy,
                    currentEmail: this.userStore.email,
                });
                const code = error?.code || "";
                if (code === "permission-denied") {
                    this.$alert(
                        "Firestore 규칙에서 삭제가 거부되었습니다.\n" +
                        `작성자 이메일: ${this.commission?.createdBy || "-"}\n` +
                        `현재 로그인: ${this.userStore.email || "-"}\n` +
                        "두 값이 다르면 다른 계정으로 작성된 글입니다.\n" +
                        "두 값이 같다면 Firebase 콘솔의 Firestore 규칙에서\n" +
                        "commissions 컬렉션의 delete 권한을 확인해주세요."
                    );
                } else {
                    this.$alert(
                        `삭제에 실패했습니다.${error?.message ? `\n(${error.message})` : ""}\n잠시 후 다시 시도해주세요.`
                    );
                }
            }
        },
        openExternal() {
            if (this.commission?.link) {
                window.open(this.commission.link, "_blank");
            }
        },
        toggleCommentSection() {
            this.showCommentSection = !this.showCommentSection;
        },
        async handleShareMethod(method) {
            this.showShareModal = false;
            if (!this.commission) return;
            const url = typeof window !== "undefined" ? window.location.href : "";
            const title = this.commission.name || "커미션";
            const id = this.commission.id;

            if (method === "kakao") {
                await shareViaKakao();
                trackShare("post", id, "kakao");
                return;
            }
            if (method === "twitter") {
                shareViaTwitter();
                trackShare("post", id, "twitter");
                return;
            }
            if (method === "copy") {
                trackShare("post", id, "copy");
                this.copyToClipboard(url);
                return;
            }
            if (method === "native" && typeof navigator !== "undefined" && navigator.share) {
                try {
                    await navigator.share({
                        title,
                        text: `${title} - 링꾹라이브 커미션`,
                        url,
                    });
                    trackShare("post", id, "native");
                } catch (error) {
                    if (error.name !== "AbortError") {
                        trackShare("post", id, "copy_fallback");
                        this.copyToClipboard(url);
                    }
                }
            }
        },
        copyToClipboard(text) {
            navigator.clipboard.writeText(text).then(() => {
                this.$alert("링크가 복사되었습니다.");
            }).catch(() => {
                const textarea = document.createElement("textarea");
                textarea.value = text;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);
                this.$alert("링크가 복사되었습니다.");
            });
        },
        onManageWrapperFocusout(e) {
            if (!e.currentTarget.contains(e.relatedTarget)) {
                this.showManageDropdown = false;
            }
        },
        categoryLabel(value) {
            const map = {
                design: "디자인",
                illustration: "일러스트",
                video: "영상",
                music: "음악",
                etc: "기타",
            };
            return map[value] || "기타";
        },
        async loadDetailImageUrls() {
            const urls = [];
            for (const fname of (this.commission?.detailImages || [])) {
                if (!fname) continue;
                try {
                    let url = fname;
                    if (!fname.startsWith("http")) {
                        const imageRef = ref(storage, `commission/${fname}`);
                        url = await getDownloadURL(imageRef);
                    }
                    urls.push(url);
                } catch {
                    // skip
                }
            }
            this.detailImageUrls = urls;
        },
        formatDate(date) {
            if (!(date instanceof Date)) return "";
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, "0");
            const d = String(date.getDate()).padStart(2, "0");
            return `${y}.${m}.${d}`;
        },
        async toggleApproval() {
            if (this.userStore.auth !== "admin" || !this.commission?.id) return;
            this.approvalLoading = true;
            try {
                const current = this.commission.approved || false;
                const next = await toggleCommissionApproval(this.commission.id, current);
                this.commission.approved = next;
                await this.notifyCommissionCreator(next);
            } catch (e) {
                console.error("커미션 승인 변경 실패:", e);
            } finally {
                this.approvalLoading = false;
            }
        },
        async notifyCommissionCreator(approved) {
            try {
                const creatorEmail = this.commission.createdBy;
                if (!creatorEmail) {
                    console.warn("등록자 정보가 없어 알림을 전송하지 않습니다.");
                    return;
                }
                const title = approved ? "커미션이 승인되었습니다" : "커미션 승인이 취소되었습니다";
                const message = approved
                    ? `등록하신 '${this.commission.name}' 커미션이 승인되었습니다.`
                    : `등록하신 '${this.commission.name}' 커미션의 승인이 취소되었습니다.`;
                await createNotification({
                    userId: creatorEmail,
                    type: "commission",
                    title,
                    message,
                    commissionId: this.commission.id,
                    read: false,
                });
            } catch (e) {
                console.error("커미션 등록자 알림 오류:", e);
            }
        },
    },
};
</script>

<style scoped>
/* ========================================
   Base (Mobile-first)
   ======================================== */
.commission-detail-wrapper {
    min-height: 100vh;
    width: 100%;
    background: linear-gradient(180deg, #f0f7ff 0%, #edf5ff 30%, #f2f8ff 60%, #f5f0fa 100%);
    padding-bottom: 24px;
}

.detail-container {
    margin: auto;
    width: 90%;
    padding: 0px 16px;
    margin-bottom: 80px;
}

.detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 16px;
}

.back-arrow-btn {
    background: transparent;
    border: none;
    font-size: 20px;
    cursor: pointer;
    padding: 6px 10px;
    color: #333;
    transition: opacity 0.2s;
}

.back-arrow-btn:hover {
    opacity: 0.7;
}

.mobile-only {
    display: flex;
}

.detail-content {
    display: flex;
    flex-direction: column;
    gap: 18px;
    align-items: stretch;
    margin-top: 10px;
}

.draft-notice-commission {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 18px;
    margin: 0 0 4px 0;
    background: rgba(102, 189, 255, 0.12);
    border: 2px dashed rgba(102, 189, 255, 0.55);
    border-radius: 10px;
    color: #1a5f8a;
    font-size: 14px;
    line-height: 1.5;
}

.draft-notice-commission .draft-notice-icon {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(102, 189, 255, 0.2);
    border-radius: 12px;
    font-size: 22px;
}

.draft-notice-commission .draft-notice-text {
    flex: 1;
    min-width: 0;
}

.draft-notice-commission .draft-notice-label {
    display: block;
    font-weight: 700;
    font-size: 15px;
    margin-bottom: 4px;
}

.draft-notice-commission .draft-notice-desc {
    margin: 0;
    font-size: 13px;
    color: #2d6a9a;
    line-height: 1.45;
}

.approval-pending-notice-commission {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 18px;
    margin: 0 0 4px 0;
    background: #fff8e1;
    border: 2px dashed #ffb74d;
    border-radius: 10px;
    color: #e65100;
    font-size: 14px;
    line-height: 1.5;
}

.approval-pending-icon-commission {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 183, 77, 0.25);
    border-radius: 12px;
    font-size: 22px;
}

.approval-pending-text-commission {
    flex: 1;
    min-width: 0;
}

.approval-pending-label-commission {
    display: block;
    font-weight: 700;
    font-size: 15px;
    margin-bottom: 4px;
}

.approval-pending-desc-commission {
    margin: 0;
    font-size: 13px;
    color: #ef6c00;
    line-height: 1.45;
}

.admin-section-commission {
    margin-top: 14px;
    border: 1px solid rgba(139, 107, 184, 0.2);
    border-radius: 12px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.75);
}

.admin-section-header-commission {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    cursor: pointer;
    font-family: "NanumSquareRoundB", sans-serif;
    font-size: 0.9rem;
    color: #2d1b4e;
}

.admin-section-header-commission:hover {
    background: rgba(139, 107, 184, 0.06);
}

.admin-section-title-commission {
    font-weight: 700;
}

.admin-section-toggle-commission {
    font-size: 0.75rem;
    opacity: 0.7;
}

.admin-section-content-commission {
    padding: 0 14px 12px;
}

.admin-info-table-commission {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.admin-info-row-commission {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    font-size: 0.85rem;
}

.admin-info-label-commission {
    min-width: 72px;
    font-weight: 700;
    color: #5c4a78;
}

.admin-info-value-commission {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    flex: 1;
}

.approval-status-commission {
    padding: 3px 10px;
    border-radius: 8px;
    font-size: 0.78rem;
    font-weight: 700;
}

.approval-status-commission--ok {
    background: rgba(76, 175, 80, 0.15);
    color: #2e7d32;
}

.approval-status-commission--wait {
    background: rgba(255, 152, 0, 0.15);
    color: #e65100;
}

.approval-toggle-btn-commission {
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid rgba(139, 107, 184, 0.35);
    background: #fff;
    font-size: 0.78rem;
    font-weight: 700;
    cursor: pointer;
    color: #2d1b4e;
}

.approval-toggle-btn-commission:hover:not(:disabled) {
    background: rgba(139, 107, 184, 0.08);
}

.approval-toggle-btn-commission:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.reserve-btn-inner--disabled {
    opacity: 0.85;
    cursor: default;
    text-align: center;
    background: rgba(120, 120, 120, 0.15) !important;
    color: #555 !important;
}

/* 포스터 영역 */
.detail-poster-container {
    width: 100%;
    height: 200px;
    position: relative;
    overflow: hidden;
    border-radius: 18px;
    background: #000;
}

.poster-blur-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: cover;
    background-position: center;
    filter: blur(4px);
    transform: scale(1.1);
    opacity: 0.3;
    z-index: 1;
}

.poster-wrapper {
    position: relative;
    display: inline-block;
    width: 100%;
    height: 100%;
}

.detail-poster {
    position: absolute;
    top: 50%;
    left: 42vw;
    transform: translate(-50%, -50%);
    max-width: 50%;
    max-height: 85%;
    width: auto;
    height: auto;
    border-radius: 0px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.3);
    z-index: 2;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.detail-poster.poster-loaded {
    opacity: 1;
}

.no-image-placeholder {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
    color: #888;
    font-size: 14px;
    font-family: "NanumSquareRoundR", sans-serif;
}

.poster-skeleton {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 160px;
    height: 220px;
    background: linear-gradient(135deg, #f0f0f0 0%, #e8e8e8 100%);
    border-radius: 8px;
    overflow: hidden;
    z-index: 11;
}

.skeleton-shimmer {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.5) 50%,
            transparent 100%);
    animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
    0% {
        transform: translateX(-100%);
    }

    100% {
        transform: translateX(100%);
    }
}

/* 하단 버튼: 링꾹 구매 → 구매하기(router .reserve-btn-inner), 그 외 → 문의하기(그라데이션) */
.reserve-btn {
    width: 100%;
}

/* LiveDetail과 동일: 화면(PC/모바일) 하단 고정 */
.mobile-reserve-btn {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 1024px;
    margin: 0 auto;
    z-index: 10;
    padding: 16px 20px calc(16px + env(safe-area-inset-bottom));
    background: #fff;
    border-top: 1px solid #e8e8e8;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.06);
}

/* LiveDetail 일반(외부) 예매 버튼과 동일 */
.reserve-btn-inner {
    display: block;
    width: 100%;
    margin: auto;
    box-sizing: border-box;
    background: #fff;
    color: #66bdff;
    border: 2px solid #66bdff;
    border-radius: 20px;
    padding: 10px 0;
    font-size: 20px;
    font-weight: bold;
    box-shadow: 0 2px 8px rgba(102, 189, 255, 0.1);
    cursor: pointer;
    transition: all 0.3s ease;
    letter-spacing: 1px;
    font-family: "NanumSquareRoundB", sans-serif;
    text-align: center;
    text-decoration: none;
}

a.reserve-btn-inner:visited {
    color: #66bdff;
}

.reserve-btn-inner:hover {
    background: #f0f8ff;
    border-color: #4a9eff;
    color: #4a9eff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 189, 255, 0.25);
}

.reserve-btn-inner:active {
    transform: translateY(0);
    background: #e3f2fd;
    box-shadow: 0 2px 6px rgba(102, 189, 255, 0.2);
}

.reserve-btn-inner.linkook-reserve {
    background: linear-gradient(135deg, #66bdff 0%, #ff69b4 100%);
    color: #fff;
    border: none;
    box-shadow: 0 4px 15px rgba(102, 189, 255, 0.4),
        0 4px 15px rgba(255, 105, 180, 0.3);
    animation: linkookButtonPulse 2s ease-in-out infinite;
}

.reserve-btn-inner.linkook-reserve:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 189, 255, 0.5),
        0 6px 20px rgba(255, 105, 180, 0.4);
    background: linear-gradient(135deg, #66bdff 0%, #ff69b4 100%);
    color: #fff;
}

.reserve-btn-inner.linkook-reserve:active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(102, 189, 255, 0.4),
        0 2px 10px rgba(255, 105, 180, 0.3);
}

@keyframes linkookButtonPulse {

    0%,
    100% {
        box-shadow: 0 4px 15px rgba(102, 189, 255, 0.4),
            0 4px 15px rgba(255, 105, 180, 0.3);
    }

    50% {
        box-shadow: 0 6px 20px rgba(102, 189, 255, 0.6),
            0 6px 20px rgba(255, 105, 180, 0.5);
    }
}

/* 상세 정보 영역 */
.detail-info {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
    width: 100%;
    padding: 0;
    margin-top: 0px;
    margin-bottom: 24px;
}

.detail-title {
    width: 100%;
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
}

.title-left {
    display: flex;
    align-items: baseline;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
}

.title-actions {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
}

.share-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s;
}

.share-btn:hover {
    opacity: 0.7;
}

.share-icon {
    width: 22px;
    height: 22px;
    object-fit: contain;
}

.detail-title h1 {
    font-size: 1.1rem;
    color: #333;
    margin: 0;
    word-break: keep-all;
    font-family: "Paperlogy8", "NanumSquareRoundEB", sans-serif;
    letter-spacing: -0.5px;
    line-height: 1.4;
}

.detail-more-link {
    font-size: 12px;
    color: #66bdff;
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;
    transition: all 0.2s;
    font-family: "NanumSquareRoundR", sans-serif;
}

.detail-more-link:hover {
    color: #4a9eff;
    text-decoration: underline;
}

/* 관리 드롭다운 */
.manage-btn-wrapper {
    position: relative;
    flex-shrink: 0;
}

.manage-settings-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s ease;
    border-radius: 6px;
}

.manage-settings-btn:hover {
    opacity: 0.7;
}

.settings-icon {
    width: 22px;
    height: 22px;
    object-fit: contain;
    opacity: 0.45;
}

.manage-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    min-width: 120px;
    z-index: 100;
    overflow: hidden;
}

.manage-dropdown-item {
    display: block;
    width: 100%;
    padding: 10px 16px;
    background: transparent;
    border: none;
    text-align: left;
    font-size: 0.9rem;
    color: #333;
    cursor: pointer;
    transition: background 0.15s;
}

.manage-dropdown-item:hover {
    background: #f5f5f5;
}

.manage-dropdown-item--danger {
    color: #e53935;
}

.manage-dropdown-item--danger:hover {
    background: #fff5f5;
}

/* 정보 테이블 */
.info-table {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
    width: 100%;
}

.info-row {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 16px;
}

.info-label {
    width: 70px;
    min-width: 70px;
    color: #888;
    font-size: 11px;
    font-weight: 600;
    text-align: left;
    font-family: "NanumSquareRoundEB", sans-serif;
    letter-spacing: -0.3px;
    padding-top: 2px;
    margin-top: 1px;
}

.info-value {
    color: #333;
    font-size: 12px;
    font-weight: 400;
    text-align: left;
    word-break: keep-all;
    font-family: "Pretendard", sans-serif;
    line-height: 1.5;
    letter-spacing: -0.2px;
}

.commission-description-block {
    margin-bottom: 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid #eee;
}

.commission-description-block .description-viewer-host {
    width: 100%;
}

.commission-description-block :deep(.toastui-editor-contents) {
    font-family: "Pretendard", "NanumSquareRoundR", sans-serif;
    font-size: 14px;
    color: #444;
    line-height: 1.7;
    padding: 0;
}

.commission-description-block :deep(.toastui-editor-contents img) {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
}

.commission-description-block :deep(.toastui-editor-contents p) {
    margin: 0 0 8px 0;
}

.commission-description-block :deep(.toastui-editor-contents a) {
    color: #66bdff;
    text-decoration: underline;
    word-break: break-all;
}

/* TinyMCE HTML 설명 블록 */
.commission-description-html .description-html-host {
    font-family: "Pretendard", "NanumSquareRoundR", sans-serif;
    font-size: 14px;
    color: #444;
    line-height: 1.7;
    text-align: left;
    word-break: break-word;
}

.commission-description-html .description-html-host :deep(p) {
    margin: 0 0 8px 0;
}

.commission-description-html .description-html-host :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
}

.commission-description-html .description-html-host :deep(a) {
    color: #66bdff;
    text-decoration: underline;
    word-break: break-all;
}

.commission-description-html .description-html-host :deep(table) {
    border-collapse: collapse;
    width: 100%;
    margin: 8px 0;
}

.commission-description-html .description-html-host :deep(table th),
.commission-description-html .description-html-host :deep(table td) {
    border: 1px solid #e0e0e0;
    padding: 6px 10px;
}

.commission-description-html .description-html-host :deep(blockquote) {
    border-left: 3px solid #ddd;
    padding: 4px 12px;
    color: #666;
    margin: 8px 0;
}

.commission-description-html .description-html-host :deep(iframe) {
    max-width: 100%;
    aspect-ratio: 16 / 9;
    height: auto;
    border: 0;
    border-radius: 6px;
}

.admin-sub-text {
    color: #999;
    font-size: 11px;
    margin-left: 4px;
}

/* 탭 섹션 */
.tabs-section {
    margin-top: 24px;
    width: 100%;
    overflow: hidden;
    margin-bottom: 70px;
    border-radius: 10px;
}

.tab-navigation {
    display: flex;
    background: #fff;
    border-bottom: 1px solid #e0e0e0;
}

.tab-button {
    flex: 1;
    padding: 12px 8px;
    border: none;
    background: none;
    font-size: 14px;
    font-weight: 500;
    color: #666;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: "NanumSquareRoundR", sans-serif;
    border-bottom: 3px solid transparent;
    border-radius: 0px;
}

.tab-button:hover {
    color: #333;
    background: #f5f5f5;
}

.tab-button.active {
    color: #66bdff;
    background: #f8fafe;
    border-bottom-color: #66bdff;
}

.tab-content {
    padding: 18px 8px 14px 8px;
    min-height: 100px;
}

.tab-panel {
    animation: fadeIn 0.3s ease-in-out;
}

/* 판매 정보 탭 — LiveDetail 판매 정보(booking) 탭과 동일 스타일 */
.commission-detail-wrapper .booking-content {
    margin: 0 auto;
}

.commission-detail-wrapper .booking-item {
    margin-bottom: 16px;
    padding: 16px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.commission-detail-wrapper .booking-item:last-child {
    margin-bottom: 0;
}

.commission-detail-wrapper .booking-label {
    font-size: 14px;
    font-weight: bold;
    color: #333;
    margin-top: 0px;
    margin-bottom: 8px;
    font-family: "NanumSquareRoundB", sans-serif;
}

.commission-detail-wrapper .booking-value {
    font-size: 14px;
    color: #666;
    margin: 0;
    font-family: "NanumSquareRoundR", sans-serif;
}

/* 세부 이미지 */
.detail-image-section {
    text-align: center;
}

.detail-image-container {
    margin-bottom: 24px;
}

.detail-image-container:last-child {
    margin-bottom: 0;
}

.detail-image {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 영상 (LiveDetail 공연상세 하단과 동일) */
.commission-youtube-block {
    width: 100%;
    margin-top: 20px;
    padding-top: 4px;
}

.artist-section-title {
    max-width: 800px;
    margin: 16px auto 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    font-size: 15px;
    color: #333;
    font-family: "NanumSquareRoundB", sans-serif;
    font-weight: 700;
}

.artist-section-title::before,
.artist-section-title::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #e5e5e5;
}

.youtube-videos-grid {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 0 4px;
}

.youtube-video-item {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.youtube-embed-wrapper {
    position: relative;
    width: 100%;
    padding-top: 56.25%;
    border-radius: 12px;
    overflow: hidden;
    background: #000;
}

.youtube-embed {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border-radius: 12px;
}

.youtube-video-meta {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 0 2px;
}

.youtube-video-title {
    font-family: "NanumSquareRoundB", sans-serif;
    font-size: 14px;
    color: #222;
    line-height: 1.4;
    margin: 0;
}

.youtube-video-channel {
    font-family: "NanumSquareRoundR", sans-serif;
    font-size: 13px;
    color: #868e96;
    margin: 0;
}

/* 댓글 오버레이 */
.comment-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 199;
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

/* 댓글 패널 */
.comment-panel {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-height: 85vh;
    background: white;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
    z-index: 200;
    transform: translateY(100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.comment-panel-open {
    transform: translateY(0);
}

.comment-panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e9ecef;
    background: white;
    position: sticky;
    top: 0;
    z-index: 10;
}

.comment-panel-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
    font-family: "NanumSquareRoundB", sans-serif;
}

.comment-panel-close {
    background: none;
    border: none;
    font-size: 28px;
    color: #666;
    cursor: pointer;
    padding: 0;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s ease;
    line-height: 1;
}

.comment-panel-close:hover {
    background: #f5f5f5;
    color: #333;
}

.comment-panel-content {
    flex: 1;
    overflow-y: auto;
    padding: 0;
}

/* ========================================
   Tablet / PC (> 480px)
   ======================================== */
@media screen and (min-width: 481px) {
    .detail-container {
        width: 90%;
        padding: 0px 16px;
    }

    .mobile-only {
        display: none;
    }

    .detail-poster-container {
        height: 350px;
    }

    .detail-poster {
        top: 39%;
        left: 50%;
        max-width: 50%;
        max-height: 70%;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    }

    .detail-title h1 {
        font-size: 1.25rem;
    }

    .info-label {
        font-size: 12px;
    }

    .info-value {
        font-size: 13px;
    }

    .reserve-btn-inner {
        font-size: 20px;
        padding: 14px 0;
        border-radius: 20px;
    }

    .comment-panel {
        max-height: 75vh;
        border-radius: 20px 20px 0 0;
    }

    .comment-panel-header {
        padding: 20px;
    }

    .comment-panel-title {
        font-size: 20px;
    }

    .comment-panel-close {
        font-size: 32px;
        width: 40px;
        height: 40px;
    }
}

/* ========================================
   Mobile (<= 480px)
   ======================================== */
@media screen and (max-width: 480px) {
    .detail-container {
        padding: 0px 10px;
        padding-bottom: 100px;
    }

    .back-arrow-btn {
        font-size: 18px;
        padding: 4px 8px;
    }

    .mobile-reserve-btn {
        padding: 12px 12px calc(12px + env(safe-area-inset-bottom));
        border-top: 1px solid #e0e0e0;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
        -webkit-tap-highlight-color: transparent;
    }

    .reserve-btn-inner {
        font-size: 15px;
        padding: 13px 0;
        border-radius: 30px;
    }

    .reserve-btn-inner.linkook-reserve:active {
        transform: scale(0.98);
    }

    .commission-description-block :deep(.toastui-editor-contents) {
        font-size: 13px;
    }

    .commission-detail-wrapper .booking-item {
        margin-bottom: 12px;
        padding: 12px;
    }

    .commission-detail-wrapper .booking-label {
        font-size: 12px;
        margin-bottom: 6px;
    }

    .commission-detail-wrapper .booking-value {
        font-size: 12px;
    }

    .tab-button {
        padding: 10px 6px;
        font-size: 12px;
    }

    .tab-content {
        padding: 12px 6px 10px 6px;
    }

    .youtube-videos-grid {
        padding: 0 0 !important;
    }

    .youtube-video-title {
        font-size: 12px;
    }

    .youtube-video-channel {
        font-size: 11px;
    }
}
</style>
